import React from "react";
import { useState, useEffect } from "react";

import {
  SpaceBetween,
  Header,
  Container,
  Box,
  FormField,
  Select,
  SelectProps,
  Button,
  Grid,
  Alert,
} from "@amzn/awsui-components-console";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import type { RootState } from "../../redux/store";
import moment from "moment";
import SimLinkGenerator from "src/components/simLinkGenerator/SimLinkGenerator";
import { SIM_TYPES } from "src/components/simLinkGenerator/types";
import {
  getFile,
  listFiles,
  setValueInWorkersCompensationDocumentsState,
} from "../workersCompensationDocuments/workersCompensationDocumentsSlice";
import { WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE } from "src/views/workersCompensationDocuments/constants";
import { ClaimFile } from "@amzn/ttechclaimintakeservice-client";
import RetrievingFileList from "../workersCompensationDocuments/RetrievingFileList";
import { FileOption } from "./types";

const ProvinceForms = () => {
  const { files, fileUrl, gettingFiles, gettingFileUrl, listFilesError, getFileUrlError } =
    useAppSelector(
      (state: RootState) =>
        state.workersCompensationDocuments[WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE]
    );

  const dispatch = useAppDispatch();
  const [fileOptions, setFileOptions] = useState<FileOption[]>([]);
  const [selectedOption, setSelectedOption] = useState<FileOption | null>(null);
  const [alert, setAlert] = useState<React.ReactNode | null>(null);

  /**
   * Get file list on load
   */
  useEffect(() => {
    dispatch(listFiles({ fileType: WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE }));
  }, []);

  /**
   * Build sorted file options from file list
   */
  useEffect(() => {
    const options = files.reduce((result: FileOption[], file: ClaimFile) => {
      const tokenizedFileName = file?.fileName?.split("/") ?? [];
      const standaloneFileName = tokenizedFileName[tokenizedFileName?.length - 1];
      if (!standaloneFileName || !file.fileName) return result;
      result.push({
        value: file.fileName,
        label: standaloneFileName,
        lastUploadTimestamp: file.lastUploadTimestamp,
      });
      return result;
    }, []);

    options.sort((a: FileOption, b: FileOption) => {
      return a.label.localeCompare(b.label);
    });
    setFileOptions(options);
  }, [files]);

  /**
   * Trigger file download when url is received
   */
  useEffect(() => {
    if (fileUrl) {
      window.open(fileUrl, "_blank");
      dispatch(
        setValueInWorkersCompensationDocumentsState({
          key: "fileUrl",
          value: "",
          fileType: WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE,
        })
      );
    }
  }, [fileUrl]);

  /**
   * Set alert if there was an error getting the fileUrl
   */
  useEffect(() => {
    if (getFileUrlError) {
      setAlert(
        <Box data-testid="getFileUrlErrorAlert">
          There was a problem retrieving the download link for this file, please try again later,
          reload the page or search the documents below for another file, if the problem persists
          please open a{" "}
          <SimLinkGenerator
            simType={SIM_TYPES.REPORT_A_BUG}
            target="_blank"
            ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
          >
            support ticket
          </SimLinkGenerator>{" "}
          to assist you with this issue.
        </Box>
      );
    } else {
      setAlert(null);
    }
  }, [getFileUrlError]);

  const getFileUrl = (fileName?: string) => {
    if (!fileName) return;
    setAlert(null);
    dispatch(getFile({ fileName, fileType: WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE }));
  };

  const renderErrorListingFiles = () => {
    return (
      <SpaceBetween direction="vertical" size="xs" data-testid="listingFilesErrorAlertContainer">
        <Alert type="error" data-testid="listingFilesErrorAlert">
          There was an error retrieving the files. Please reload the page or click on the button
          below to try again. If the error persists please open a{" "}
          <SimLinkGenerator
            simType={SIM_TYPES.REPORT_A_BUG}
            target="_blank"
            ariaLabel="Pressing this link will open a new tab where you will be able to create a ticket for the team to investigate this issue."
          >
            support ticket
          </SimLinkGenerator>{" "}
          to get help with this issue.
        </Alert>
        <Button
          variant="normal"
          onClick={() => {
            dispatch(listFiles({ fileType: WC_CANADA_PROVINCE_FORM_DOCUMENT_TYPE }));
          }}
        >
          Reload File List
        </Button>
      </SpaceBetween>
    );
  };

  const renderErrorAlert = () => {
    if (alert) {
      return (
        <Alert type="error" data-testid="provinceFormsErrorAlert">
          {alert}
        </Alert>
      );
    }
  };

  return (
    <SpaceBetween direction="vertical" size="s">
      <Container header={<Header variant="h2">Workers&apos; Compensation Province Forms</Header>}>
        <SpaceBetween direction="vertical" size="s">
          {listFilesError ? (
            renderErrorListingFiles()
          ) : gettingFiles ? (
            <RetrievingFileList />
          ) : (
            <SpaceBetween size="s" direction="vertical">
              {renderErrorAlert()}

              <FormField
                description="Search from the dropdown menu for the currently available Workers' Compensation Province Forms"
                data-testid="workersCompensationProvinceFormsFormField"
              >
                <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
                  <Select
                    data-testid="province-forms-dropdown"
                    options={fileOptions}
                    selectedOption={selectedOption}
                    onChange={({ detail }: { detail: SelectProps.ChangeDetail }) => {
                      setSelectedOption(detail.selectedOption as FileOption);
                    }}
                    filteringType="auto"
                    placeholder="Select a file to download"
                    virtualScroll={true}
                  ></Select>
                  <Button
                    data-testid="province-forms-download-btn"
                    disabled={!selectedOption || gettingFileUrl}
                    variant="primary"
                    onClick={() => {
                      getFileUrl(selectedOption?.value);
                    }}
                    loading={gettingFileUrl}
                  >
                    Download
                  </Button>
                </Grid>
                {selectedOption ? (
                  <div>
                    <span>
                      <b>Date Uploaded: </b>
                      {`${moment(selectedOption.lastUploadTimestamp).format("LLLL")}`}
                    </span>
                  </div>
                ) : null}
              </FormField>
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};

export default ProvinceForms;
